import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  ResendConfirmationCodeCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  SetUserMFAPreferenceCommand,
  AuthFlowType
} from "@aws-sdk/client-cognito-identity-provider";

import { 
  SignUpFormData, 
  UserSession
} from '../../types/auth';
import { TokenService } from './token.service';
import { cognitoConfig } from '../../config/app.config';

interface CognitoSdkError { name?: string; message?: string }

const RATE_LIMIT_MESSAGE = 'Too many attempts. Please wait a few minutes and try again.';
export const ACCOUNT_SETUP_FAILED_MESSAGE =
  'Your email is verified, but we could not finish setting up your organization. Please contact support before signing in.';
const isRateLimitError = (err: CognitoSdkError): boolean =>
  err?.name === 'LimitExceededException' || err?.name === 'TooManyRequestsException';

// Create Cognito client. Public-client calls only: no AWS credentials in the browser.
export const createCognitoClient = (region: string): CognitoIdentityProviderClient => {
  return new CognitoIdentityProviderClient({ region });
};

// Confirm sign-up (email verification)
export const confirmSignUp = async (
  client: CognitoIdentityProviderClient,
  username: string,
  confirmationCode: string,
  clientId: string
) => {
  const command = new ConfirmSignUpCommand({
    ClientId: clientId,
    Username: username,
    ConfirmationCode: confirmationCode
  });
    
  return await client.send(command);
};

// Set up MFA
export const setupMfa = async (
  client: CognitoIdentityProviderClient,
  session: string
) => {
  const setupCommand = new AssociateSoftwareTokenCommand({
    Session: session
  });
    
  return await client.send(setupCommand);
};

// Generate QR code URL for authenticator app
export const generateQrCodeUrl = (secretCode: string, username: string) => {
  const issuer = encodeURIComponent('Pelora Health');
  const encodedUsername = encodeURIComponent(username);
  return `otpauth://totp/${issuer}:${encodedUsername}?secret=${secretCode}&issuer=${issuer}`;
};

// Verify MFA setup
export const verifyMfaSetup = async (
  client: CognitoIdentityProviderClient,
  session: string,
  code: string
) => {
  const verifySoftwareTokenCommand = new VerifySoftwareTokenCommand({
    Session: session,
    UserCode: code
  });
    
  return await client.send(verifySoftwareTokenCommand);
};

/**
 * Get user attributes from token
 */
export const getUserAttributesFromToken = (idToken: string) => {
  try {
    if (!idToken) {
      console.warn('getUserAttributesFromToken: No ID token provided');
      return null;
    }
    
    // ID token is a JWT. Extract the payload
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      console.warn('getUserAttributesFromToken: ID token is not a valid JWT (should have 3 parts)');
      return null;
    }
    
    const payload = parts[1];
    const paddedPayload = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '=');
    const decodedPayload = atob(paddedPayload);
    const userData = JSON.parse(decodedPayload);
    
    // Validate critical fields
    if (!userData['sub']) {
      console.warn('getUserAttributesFromToken: No "sub" field in token payload - user ID will be missing');
    }
    
    // Look for the tenant ID in different possible formats (case-sensitive in Cognito)
    const tenantId = userData['custom:TenantID'] || userData['custom:tenant_id'] || userData['custom:tenantId'] || '';
    
    // Extract provider type from custom attribute
    const providerType = userData['custom:provider_type'] || '';
    
    // Extract user type from custom attribute (EMPLOYEE or GUARDIAN)
    const userType = userData['custom:user_type'] || '';
    
    return {
      email: userData['email'] || '',
      firstName: userData['given_name'] || '',
      lastName: userData['family_name'] || '',
      sub: userData['sub'] || '',
      tenantId: tenantId,
      providerType: providerType || undefined,
      userType: userType || undefined,
      groups: (userData['cognito:groups'] as string[] | undefined) || []
    };
  } catch (error) {
    console.error('Error parsing ID token:', error);
    return null;
  }
};

// Get user attributes
export const getUserAttributes = async (
  client: CognitoIdentityProviderClient,
  accessToken: string
) => {
  try {
    const getUserDetails = new GetUserCommand({ AccessToken: accessToken });
    const result = await client.send(getUserDetails);
    return result;
  } catch (error) {
    console.error('Error getting user attributes:', error);
    throw error;
  }
};

// Shown when no reset code can be sent: an invited user who never signed in (temporary password
// not changed yet) or an account without a verified email. Same copy for both, so it does not
// reveal which case applies (ACC-06).
export const RESET_UNAVAILABLE_MESSAGE =
  "We couldn't send a reset code for this account. If you were invited and haven't signed in yet, " +
  'sign in with the temporary password from your invitation email, or ask your administrator to resend the invitation.';

/**
 * Forgot password: send the reset code. Resolves when Cognito accepted the request.
 * An unknown email resolves too (no account enumeration; the pool also hides it with
 * PreventUserExistenceErrors). Rejects with a message that is safe to show.
 */
export const forgotPassword = async (
  client: CognitoIdentityProviderClient,
  email: string,
  clientId: string
) => {
  try {
    return await client.send(new ForgotPasswordCommand({
      ClientId: clientId,
      Username: email.trim()
    }));
  } catch (err) {
    const name = (err as CognitoSdkError)?.name;
    if (name === 'UserNotFoundException') return undefined;
    if (isRateLimitError(err as CognitoSdkError)) throw new Error(RATE_LIMIT_MESSAGE);
    if (name === 'NotAuthorizedException' || name === 'InvalidParameterException') {
      throw new Error(RESET_UNAVAILABLE_MESSAGE);
    }
    throw new Error('Unable to send a reset code right now. Please try again later.');
  }
};

// Resend verification code
export const resendVerificationCode = async (
  client: CognitoIdentityProviderClient,
  username: string,
  clientId: string
) => {
  try {
    return await client.send(new ResendConfirmationCodeCommand({
      ClientId: clientId,
      Username: username.trim()
    }));
  } catch (err) {
    if (isRateLimitError(err as CognitoSdkError)) throw new Error(RATE_LIMIT_MESSAGE);
    throw new Error('Unable to resend the code right now. Please try again later.');
  }
};

// Save session: access/ID tokens in memory, refresh token in sessionStorage
export const saveSession = (session: UserSession) => {
  TokenService.saveTokens(session);
};

// Clear the session from memory and storage
export const clearSession = () => {
  TokenService.clearTokens();
};

/**
 * Exchange the refresh token for new access/ID tokens (REFRESH_TOKEN_AUTH).
 * Returns false when there is no refresh token or Cognito rejects it.
 */
export const refreshSession = async (): Promise<boolean> => {
  const refreshToken = TokenService.getRefreshToken();
  if (!refreshToken) {
    return false;
  }
  try {
    const result = await createCognitoClient(cognitoConfig.region).send(new InitiateAuthCommand({
      ClientId: cognitoConfig.clientId,
      AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
      AuthParameters: { REFRESH_TOKEN: refreshToken }
    }));
    const auth = result.AuthenticationResult;
    if (!auth?.AccessToken || !auth?.IdToken) {
      return false;
    }
    // REFRESH_TOKEN_AUTH does not return a new refresh token; keep the current one
    TokenService.saveTokens({ accessToken: auth.AccessToken, idToken: auth.IdToken });
    return true;
  } catch {
    return false;
  }
};

/**
 * Sign out everywhere: revoke refresh tokens in Cognito (GlobalSignOut),
 * then clear local tokens. Local tokens are cleared even if the call fails.
 */
export const globalSignOut = async (): Promise<void> => {
  const accessToken = TokenService.getAccessToken();
  try {
    if (accessToken) {
      await createCognitoClient(cognitoConfig.region).send(new GlobalSignOutCommand({ AccessToken: accessToken }));
    }
  } catch {
    // Token already expired or revoked: nothing more to revoke
  } finally {
    TokenService.clearTokens();
  }
};

/**
 * ACC-05: start TOTP enrollment for a signed-in user (enrollment gate).
 * Returns the otpauth:// URL for the QR code.
 */
export const startTotpEnrollment = async (username: string): Promise<string> => {
  const accessToken = TokenService.getAccessToken();
  if (!accessToken) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  const result = await createCognitoClient(cognitoConfig.region).send(
    new AssociateSoftwareTokenCommand({ AccessToken: accessToken })
  );
  if (!result.SecretCode) {
    throw new Error('Failed to get MFA secret code');
  }
  return generateQrCodeUrl(result.SecretCode, username);
};

/**
 * ACC-05: verify the first TOTP code and make TOTP the preferred MFA method.
 */
export const completeTotpEnrollment = async (code: string): Promise<void> => {
  const accessToken = TokenService.getAccessToken();
  if (!accessToken) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  const client = createCognitoClient(cognitoConfig.region);
  const verify = await client.send(new VerifySoftwareTokenCommand({ AccessToken: accessToken, UserCode: code }));
  if (verify.Status !== 'SUCCESS') {
    throw new Error('Invalid MFA code');
  }
  await client.send(new SetUserMFAPreferenceCommand({
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true }
  }));
};


// In-memory temporary storage for signup flow (never store passwords in localStorage!)
let tempSignupData: { email?: string; organizationName?: string } = {};

// Save temporary data for account verification flow
export const saveTempSignupData = (signupData: SignUpFormData) => {
  // SECURITY: Store only non-sensitive data in memory, never passwords
  tempSignupData = {
    email: signupData.email,
    organizationName: signupData.organizationName
  };
  // Password is intentionally NOT stored
};

// Get temporary signup data
export const getTempSignupData = () => {
  return tempSignupData;
};

// Clear temporary signup data
export const clearTempSignupData = () => {
  tempSignupData = {};
  tempTokens = {};
};

// Temporary tokens held in memory only (never browser storage)
let tempTokens: Partial<UserSession> = {};

// Save temporary tokens
export const saveTempTokens = (session: UserSession) => {
  tempTokens = { ...session };
};

// Get temporary tokens
export const getTempTokens = (): Partial<UserSession> => {
  return {
    idToken: tempTokens.idToken || '',
    accessToken: tempTokens.accessToken || '',
    refreshToken: tempTokens.refreshToken || '',
  };
};

// Export simplified function interfaces for the refactored components

/**
 * Sign up function
 */
export const signUp = async ({
  cognitoClient,
  username,
  password,
  clientId,
  userAttributes,
  validationData
}: {
  cognitoClient: CognitoIdentityProviderClient;
  username: string;
  password: string;
  clientId: string;
  /**
   * Cognito ValidationData for the PreSignUp trigger only (never stored on the user). Demo sandbox:
   * { fictionalDataConsent: 'yes', turnstileToken } (demo-sandbox plan §3.2).
   */
  validationData?: Record<string, string>;
  userAttributes: {
    given_name: string;
    family_name: string;
    email: string;
    organization_name: string;
    /** Trial plan chosen at sign-up ('starter' | 'core'); the PostConfirmation trigger reads it. */
    subscription_tier: string;
    user_agent: string;
  };
}) => {
  try {
    const command = new SignUpCommand({
      ClientId: clientId,
      Username: username,
      Password: password,
      UserAttributes: [
        {
          Name: 'given_name',
          Value: userAttributes.given_name
        },
        {
          Name: 'family_name',
          Value: userAttributes.family_name
        },
        {
          Name: 'email',
          Value: userAttributes.email
        },
        {
          Name: 'middle_name',
          Value: 'NA' 
        },
        {
          Name: 'custom:organization_name',
          Value: userAttributes.organization_name
        },
        {
          Name: 'custom:subscription_tier',
          Value: userAttributes.subscription_tier
        },
        {
          Name: 'custom:user_agent',
          Value: userAttributes.user_agent
        }
      ],
      ...(validationData
        ? { ValidationData: Object.entries(validationData).map(([Name, Value]) => ({ Name, Value })) }
        : {})
    });
    
    await cognitoClient.send(command);
    return { success: true };
  } catch (err: any) {
    if (err.name === 'UsernameExistsException') {
      // ACC-06 / EX-04: neutral copy that does not confirm the account exists
      throw new Error('If this email is registered, sign in instead.');
    } else if (isRateLimitError(err)) {
      throw new Error(RATE_LIMIT_MESSAGE);
    } else {
      throw new Error(err.message || 'Failed to sign up');
    }
  }
};

/**
 * Verify email function
 */
export const verifyEmail = async ({
  cognitoClient,
  username,
  confirmationCode,
  clientId
}: {
  cognitoClient: CognitoIdentityProviderClient;
  username: string;
  confirmationCode: string;
  clientId: string;
}) => {
  try {
    const command = new ConfirmSignUpCommand({
      ClientId: clientId,
      Username: username.trim(),
      ConfirmationCode: confirmationCode.trim()
    });
    
    await cognitoClient.send(command);
    return { success: true };
  } catch (err) {
    const e = err as CognitoSdkError;
    // Already confirmed (e.g. a second click, or the first attempt's response was lost)
    if (e?.name === 'NotAuthorizedException' && /current status is confirmed/i.test(e.message || '')) {
      return { success: true };
    }
    // Cognito confirms the user before the PostConfirmation trigger (createTenant) runs: when the
    // trigger fails the email is verified but the organization was not created, and a retry
    // cannot run the trigger again. Do not send the user on to sign in.
    if (e?.name === 'UserLambdaValidationException' && /postconfirmation/i.test(e.message || '')) {
      throw new Error(ACCOUNT_SETUP_FAILED_MESSAGE);
    }
    if (e?.name === 'CodeMismatchException') throw new Error('Invalid verification code. Check the code and try again.');
    if (e?.name === 'ExpiredCodeException') throw new Error('This code has expired. Use "Resend code" to get a new one.');
    if (isRateLimitError(e)) throw new Error(RATE_LIMIT_MESSAGE);
    throw new Error('Unable to verify your email right now. Please try again.');
  }
};

/**
 * Confirm forgot password function
 */
export const confirmForgotPassword = async ({
  cognitoClient,
  username,
  confirmationCode,
  newPassword,
  clientId
}: {
  cognitoClient: CognitoIdentityProviderClient;
  username: string;
  confirmationCode: string;
  newPassword: string;
  clientId: string;
}) => {
  try {
    await cognitoClient.send(new ConfirmForgotPasswordCommand({
      ClientId: clientId,
      Username: username.trim(),
      ConfirmationCode: confirmationCode.trim(),
      Password: newPassword
    }));
    return { success: true };
  } catch (err) {
    const e = err as CognitoSdkError;
    // The PostConfirmation trigger (createTenant) also runs after a reset and Cognito reports its
    // failure here, but it runs after the new password is saved: the reset itself succeeded.
    if (e?.name === 'UserLambdaValidationException' && /postconfirmation/i.test(e.message || '')) {
      return { success: true };
    }
    if (e?.name === 'CodeMismatchException') throw new Error('Invalid verification code. Check the code and try again.');
    if (e?.name === 'ExpiredCodeException') throw new Error('This code has expired. Use "Resend code" to get a new one.');
    if (e?.name === 'InvalidPasswordException') {
      throw new Error('Password does not meet the requirements. Please choose a stronger password.');
    }
    if (isRateLimitError(e)) throw new Error(RATE_LIMIT_MESSAGE);
    throw new Error('Unable to reset your password right now. Please try again.');
  }
};

const sessionExpired = (err: CognitoSdkError): boolean =>
  err?.name === 'InvalidSessionException' ||
  err?.name === 'NotAuthorizedException' ||
  !!err?.message?.toLowerCase().includes('session');

/**
 * MFA_SETUP challenge, step 1: AssociateSoftwareToken with the challenge session
 * (direct to Cognito; the final RespondToAuthChallenge goes through /auth/challenge).
 */
export const setupMFA = async ({
  cognitoClient,
  session,
  username
}: {
  cognitoClient: CognitoIdentityProviderClient;
  session: string;
  username: string;
}) => {
  if (!session) {
    throw new Error('Your session has expired. Please sign in again to set up MFA.');
  }
  try {
    const associateResult = await cognitoClient.send(new AssociateSoftwareTokenCommand({ Session: session }));
    if (!associateResult.SecretCode) {
      throw new Error('Failed to get MFA secret code');
    }
    return {
      secretCode: associateResult.SecretCode,
      qrCodeUrl: generateQrCodeUrl(associateResult.SecretCode, username),
      // AssociateSoftwareToken consumes the session and returns a new one
      session: associateResult.Session
    };
  } catch (err) {
    const e = err as CognitoSdkError;
    console.error('MFA setup error:', e?.name);
    if (sessionExpired(e)) {
      throw new Error('Your session has expired. Please sign in again to set up MFA.');
    }
    throw new Error(e?.message || 'Failed to setup MFA');
  }
};

/**
 * MFA_SETUP challenge, step 2: VerifySoftwareToken with the session.
 * Returns the new session to send with the MFA_SETUP challenge response.
 */
export const verifyMFA = async ({
  cognitoClient,
  session,
  code
}: {
  cognitoClient: CognitoIdentityProviderClient;
  session: string;
  code: string;
}): Promise<{ success: boolean; session?: string }> => {
  if (!session) {
    throw new Error('Your session has expired. Please sign in again to complete the MFA setup.');
  }
  try {
    const result = await cognitoClient.send(new VerifySoftwareTokenCommand({ Session: session, UserCode: code }));
    return { success: result.Status !== 'ERROR', session: result.Session };
  } catch (err) {
    const e = err as CognitoSdkError;
    console.error('MFA verification error:', e?.name);
    if (e?.name === 'CodeMismatchException' || e?.name === 'EnableSoftwareTokenMFAException') {
      throw new Error('Invalid MFA code');
    }
    if (sessionExpired(e)) {
      throw new Error('Your session has expired. Please sign in again to complete the MFA setup.');
    }
    throw new Error(e?.message || 'Failed to verify MFA code');
  }
};
